import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OtpInput } from "@/components/OtpInput";
import { publicApi, apiError } from "@/lib/publicApi";

/**
 * Phone / email input that must be OTP-verified. Shows a tick inside the field once
 * verified and clears verification the moment the value is edited.
 */
export function VerifiedField({ channel, label, value, onChange, token, onVerified, eventId, inputProps = {}, testId }) {
  const [stage, setStage] = useState("idle"); // idle | sending | code | checking
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [cooldown, setCooldown] = useState(0);
  const verified = !!token;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async () => {
    if (stage === "sending") return;
    if (!value?.trim()) return toast.error(`Enter your ${channel === "phone" ? "WhatsApp number" : "email"} first`);
    setStage("sending");
    try {
      const r = channel === "phone"
        ? await publicApi.requestPhoneCode(eventId, value.trim())
        : await publicApi.requestEmailCode(eventId, value.trim());
      setCode(["", "", "", "", "", ""]);
      setStage("code");
      setCooldown(r.resend_after_seconds || 30);
      if (r.mock_otp) {
        toast.success(`Mock OTP: ${r.mock_otp}`, { duration: 15000 });
        setCode(String(r.mock_otp).split(""));
        confirm(String(r.mock_otp));
      } else {
        toast.success(channel === "phone" ? `Code sent on WhatsApp to ${r.sent_to}` : `Code emailed to ${r.sent_to}`);
      }
    } catch (e) { setStage("idle"); toast.error(apiError(e)); }
  };

  const confirm = async (full) => {
    setStage("checking");
    try {
      const r = channel === "phone"
        ? await publicApi.confirmPhoneCode(eventId, value.trim(), full)
        : await publicApi.confirmEmailCode(eventId, value.trim(), full);
      onVerified(r.token);
      setStage("idle");
      toast.success(`${channel === "phone" ? "WhatsApp number" : "Email"} verified`);
    } catch (e) {
      setStage("code");
      setCode(["", "", "", "", "", ""]);
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <div className="field-label" style={{ marginBottom: 0 }}>
        <span>{label}</span>
        <div className="relative">
          <input
            {...inputProps}
            data-testid={testId}
            className="input-x"
            style={{ paddingRight: verified ? 44 : 104 }}
            value={value}
            onChange={(e) => { onChange(e.target.value); if (verified) { onVerified(null); } setStage("idle"); setCooldown(0); }}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            <AnimatePresence mode="wait">
              {verified ? (
                <motion.span key="ok" data-testid={`${testId}-verified`} title="Verified"
                  initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 16 }}
                  style={{ color: "var(--green)" }}>
                  <BadgeCheck size={20} />
                </motion.span>
              ) : (
                <motion.button key="verify" type="button" data-testid={`${testId}-verify-btn`}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={sendCode} disabled={stage === "sending" || cooldown > 0}
                  className="mono-label"
                  style={{ color: cooldown > 0 ? "var(--muted)" : "var(--red-2)", background: "none", border: "none", cursor: cooldown > 0 ? "not-allowed" : "pointer" }}>
                  {stage === "sending" ? <Loader2 size={14} className="animate-spin" />
                    : cooldown > 0 ? `${cooldown}s` : stage === "code" ? "RESEND" : "VERIFY"}
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {!verified && (stage === "code" || stage === "checking") && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden" data-testid={`${testId}-otp-box`}>
            <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
              Enter the 6-digit code {channel === "phone" ? "sent on WhatsApp" : "sent to your inbox"}
            </div>
            <div className="mt-2 mb-4">
              <OtpInput value={code} onChange={setCode} onComplete={confirm}
                disabled={stage === "checking"} testIdPrefix={`${testId}-otp`} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {verified && <div className="mono-label mt-2 mb-4" style={{ color: "var(--green)" }}>Verified</div>}
      {!verified && stage === "idle" && <div style={{ marginBottom: 16 }} />}
    </div>
  );
}
